import { useRef, useState, useEffect } from 'react';
import * as _ from 'lodash';
import { ViewerAsset } from '@gorilla/widgets-viewer/app/viewer/types';
import { getBoardItems, getAssets } from '@gorilla/common/src/lib/monday-api/api';
import { boardItemMondayToBoardItemEngine } from '@gorilla/common/src/lib/engine/engine';
import { toViewerFormat, getBoardAndSubboard, getAssetIdsFromEngineItems } from '@gorilla/widgets-shared/src/services/publish';
import type { Settings } from '@gorilla/widgets-viewer/app/viewer/types';
import { mondayClient } from '../services/monday';

export function useItemLoader(settings?: Settings, boardId?: number, boardFiltersGraphQL?: string) {
  const [status, setStatus] = useState('initializing');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<any>(null);
  const [data, setData] = useState<any>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!boardId) {
      return;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setStatus('initializing');
    setProgress(null);
    setData(null);
    // without this a single failed run would stick, since nothing else ever clears it
    setError(null);

    (async () => {
      try {
        const { board, subboard } = await getBoardAndSubboard(mondayClient, boardId, !!settings?.enableSubitemsSupport);

        if (!board) {
          throw new Error(`Failed to fetch board ${boardId}`);
        }

        const items = await getBoardItems(mondayClient, boardId, {
          abortSignal: abortController.signal,
          queryParams: boardFiltersGraphQL,
          onProgress: (progress) => {
            setStatus('fetching-items');
            setProgress(progress);
          },
          baseBoardFieldsOptions: {
            withFormulaValue: !!settings?.enableFormulaSupport,
          },
          withSubitems: !!settings?.enableSubitemsSupport,
        });

        const itemsEngine = items.map((item) =>
          boardItemMondayToBoardItemEngine(board, item, subboard, { formula_resolution: 'from_api' }),
        );

        if (!itemsEngine) {
          throw new Error(`Failed to fetch board items ${boardId}`);
        }

        const assetIds = getAssetIdsFromEngineItems(itemsEngine);

        let assets: ViewerAsset[] = [];

        if (assetIds.length) {
          assets = ((await getAssets(mondayClient, assetIds)) || []).map((asset) => ({
            id: parseInt(asset.id, 10),
            name: asset.name,
            extension: asset.file_extension,
            size: asset.file_size,
            uploaded: true,
            public_url: asset.public_url,
          }));
        }

        const data = {
          ...toViewerFormat(board, itemsEngine, subboard),
          assets,
        };

        if (abortController.signal.aborted) {
          return;
        }

        setData(data);
      } catch (err) {
        // this run aborted its own controller because it was superseded or the hook unmounted.
        // that is deliberate, so it must not surface as a load error - and it must not overwrite
        // the state of the run that replaced it.
        if (abortController.signal.aborted) {
          return;
        }

        try {
          // @ts-ignore
          err.boardId = boardId;
        } catch {}

        setError(err);
        setStatus('error');
        setProgress(null);
      }
    })();
  }, [boardId, boardFiltersGraphQL, settings?.enableFormulaSupport, settings?.enableSubitemsSupport]);

  let loadingMessage = 'Loading 0%';

  if (status === 'error') {
    loadingMessage = 'Error loading item data';
  } else if (progress !== null) {
    loadingMessage = `Loading ${progress}%`;
  }

  return {
    error,
    status,
    progress,
    loadingMessage,
    data,
  };
}
